import { useParams } from "react-router-dom";
import styled from "styled-components";
import { useQuery } from "@apollo/client";
import useAuth from "../../hooks/useAuth";
import PriorityInfo from "../../components/PriorityInfo/PriorityInfo";
import PriorityViewForm from "../../components/PriorityViewForm/PriorityViewForm";
import getPriority from "../../graphql/getPriority";
import getOrgFeatures from "../../graphql/getOrgFeatures";
import ScreenLoader from "../../components/ScreenLoader/ScreenLoader";
import Comment from "../../components/Comment/Comment";
import Heading from "../../components/Heading/Heading";

const StyledHeading = styled(Heading)`
  margin-bottom: 30px;
`;

const Box = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 45px;
  margin-bottom: 44px;
  color: var(--color-text-secondary);
  font-size: 14px;

  @media only screen and (max-width: 920px) {
    padding: 0 18px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  grid-gap: 30px;
  margin-bottom: 50px;

  *:nth-child(2) {
    grid-column: 1 / 2;
  }

  & > *:last-child {
    grid-row: 1 / 2;
    grid-column: 2 / 3;
  }

  @media only screen and (max-width: 1000px) {
    grid-template-columns: 1fr;

    & > *:last-child {
      grid-row: revert;
      grid-column: revert;
      max-width: revert;
    }
  }
`;

const DescriptionWrapper = styled.div`
  background-color: var(--color-bg-sidebar);
  border-radius: 10px;
  border: 1px solid var(--color-bg-sidebar);
  padding: 10px;
  min-height: 100px;
  hyphens: auto;
  line-height: 1.4;
  color: var(--color-text-primary);

  /* Rich text styles for Jira HTML descriptions */
  p { margin: 0.5em 0; }
  h1, h2, h3, h4, h5, h6 { margin: 0.75em 0 0.25em; font-weight: 600; }
  h1 { font-size: 1.4em; }
  h2 { font-size: 1.25em; }
  h3 { font-size: 1.1em; }
  ul, ol { margin: 0.5em 0; padding-left: 1.5em; }
  li { margin: 0.25em 0; }
  blockquote {
    margin: 0.5em 0;
    padding-left: 1em;
    border-left: 3px solid var(--color-text-secondary);
    color: var(--color-text-secondary);
  }
  pre {
    margin: 0.5em 0;
    padding: 0.75em;
    background-color: var(--color-bg-primary);
    border-radius: 6px;
    overflow-x: auto;
  }
  code {
    font-family: monospace;
    font-size: 0.9em;
  }
  :not(pre) > code {
    background-color: var(--color-bg-primary);
    padding: 0.15em 0.3em;
    border-radius: 3px;
  }
  a {
    color: var(--color-primary);
    text-decoration: underline;
  }
  table {
    border-collapse: collapse;
    margin: 0.5em 0;
    width: 100%;
  }
  th, td {
    border: 1px solid var(--color-text-secondary);
    padding: 0.4em 0.6em;
    text-align: left;
  }
  th { font-weight: 600; }
  hr {
    margin: 0.75em 0;
    border: none;
    border-top: 1px solid var(--color-text-secondary);
  }
`;

const ErrorMsg = styled.p`
  color: red;
`;

const PriorityDetails = () => {
  const params = useParams();
  const { selectedOrg } = useAuth();
  const orgFeatures = useQuery(getOrgFeatures, {
    variables: { id: selectedOrg.value },
  });
  const priority = useQuery(getPriority, {
    variables: { id: params.id },
    context: { version: 4 },
  });
  const loading = priority.loading || orgFeatures.loading;
  const errors = priority.error || orgFeatures.error;
  if (loading) {
    return <ScreenLoader />;
  }
  if (errors) {
    return <ErrorMsg>{errors}</ErrorMsg>;
  }
  return (
    <Box>
      <StyledHeading>{priority.data.getIssue.title}</StyledHeading>
      <Grid>
        {priority.data.getIssue.description && (
          <DescriptionWrapper>
            {/<[a-z][\s\S]*>/i.test(priority.data.getIssue.description) ? (
              <div dangerouslySetInnerHTML={{ __html: priority.data.getIssue.description }} />
            ) : (
              <p style={{ whiteSpace: "pre-line" }}>
                {priority.data.getIssue.description}
              </p>
            )}
          </DescriptionWrapper>
        )}
        <PriorityViewForm priorityId={params.id} />
        <PriorityInfo
          taskId={priority.data.getIssue.id}
          status={priority.data.getIssue.status}
          orgFeatures={orgFeatures.data?.getOrganization?.harborFeatures || []}
          organization={selectedOrg.label}
          hoursWorked={priority.data.getIssue.hoursWorked}
          hoursEstimate={priority.data.getIssue.hoursEstimate}
          recommendationId={
            (() => {
              const originalLink = priority.data.getIssue.originalLink;
              if (originalLink?.startsWith("rec:")) { // Its a recommendation
                const recId = originalLink.split(":")[1];
                return (
                  <a
                    href={`/recommendations/${recId}`}
                    rel="noopener noreferrer"
                  >
                    Recommendation 
                  </a>
                );
              }
          
              // Check if it's a valid recommendation ID format (8:4:4:4:12 - 32 characters)
              // This is a check for ids that dont have rec: and it is a recommendation
              const recIdPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/;
              if (originalLink?.length === 36 && recIdPattern.test(originalLink)) {
                return (
                  <a
                    href={`/recommendations/${originalLink}`}
                    rel="noopener noreferrer"
                  >
                    Recommendation
                  </a>
                );
              }
          
              return null; // Fallback for unrecognized links
            })()
          }
          supportCase={
            (() => {
              const originalLink = priority.data.getIssue.originalLink;
              if (originalLink?.startsWith("ticket:")){
                  return (
                    <a
                      href={`/support-cases/${priority.data.getIssue.originalLink.split(":")[1]}`}
                      rel="noopener noreferrer"
                    >
                      #{priority.data.getIssue.originalLink.split(":")[1]}
                    </a>
                );
              }
              return null; // Fallback for unrecognized links
            })()
          }
        />
      </Grid>
      <Heading>Comments {priority.data.getIssue.comments.length}</Heading>
      {priority.data.getIssue.comments.length > 0 &&
        [...priority.data.getIssue.comments].map((comment) => (
          <Comment
            key={comment.id}
            identifier={comment.email}
            text={comment.body}
            created={comment.created}
          />
        ))}
    </Box>
  );
};

export default PriorityDetails;
